/**
 * SEO Head Component
 *
 * Manages meta tags, title, and Open Graph data for SEO optimization.
 * Works with react-helmet-async to update document head dynamically.
 *
 * Usage:
 * ```tsx
 * <SEOHead
 *   title="Page Title"
 *   description="Page description for search engines"
 *   canonical="https://houston.toolboxlogic.com/page"
 * />
 * ```
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';

export interface SEOHeadProps {
  /** Page title (appends " | ToolboxLogic" automatically) */
  title: string;
  /** Meta description for search engines (150-160 characters recommended) */
  description: string;
  /** Canonical URL for this page */
  canonical?: string;
  /** Open Graph title (defaults to title if not provided) */
  ogTitle?: string;
  /** Open Graph description (defaults to description if not provided) */
  ogDescription?: string;
  /** Open Graph image URL (absolute path) */
  ogImage?: string;
  /** Open Graph type (default: 'website') */
  ogType?: 'website' | 'article' | 'profile';
  /** Additional keywords for search engines */
  keywords?: string;
  /** Twitter card type (default: 'summary_large_image') */
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
  /** Disable indexing for this page (noindex, nofollow) */
  noIndex?: boolean;
  /** Additional JSON-LD structured data */
  jsonLd?: Record<string, unknown>;
}

export function SEOHead({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  keywords,
  twitterCard = 'summary_large_image',
  noIndex = false,
  jsonLd,
}: SEOHeadProps) {
  const fullTitle = title.includes('ToolboxLogic') ? title : `${title} | ToolboxLogic`;
  const finalOgTitle = ogTitle || fullTitle;
  const finalOgDescription = ogDescription || description;

  return (
    <Helmet prioritizeSeoTags>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      {canonical && <meta property="og:url" content={canonical} />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta property="og:site_name" content="ToolboxLogic" />

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={finalOgTitle} />
      <meta name="twitter:description" content={finalOgDescription} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      {/* JSON-LD Structured Data */}
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
}
